import PaymentManualCreditDebitView from 'app/src/views/payments/PaymentManualCreditDebitView';
import Localize from 'app/src/localize/Localize';
import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import AppViewManager from 'app/AppViewManager';
import Sale from 'app/src/data/models/Sale';
import Customer from 'app/src/data/models/Customer';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Navigation from 'app/Navigation';
import get from 'lodash/get';

const CARD_NOT_PRESENT = 'card_not_present';
const ON = 'on';

export default class PaymentManualCreditDebitViewController extends BasePaymentController {
  /**
   * ChildView for when the Manual Button is clicked on from the Credit/Debit screen.
   *
   * @param {SaleManager}      saleManager        The sale manager instance
   * @param {PaymentProvider}  paymentProvider    The payment provider instance
   * @param {Customer}         customer   The customer attached to the payment
   * @param {Decimal}          amount     The amount to be transacted
   * @param {Boolean}          isDeposit  When true, the payment will be treated as a deposit payment
   */
  constructor(saleManager, paymentProvider, customer, amount, isDeposit = false) {
    if (!(paymentProvider instanceof PaymentProvider)) {
      throw 'A payment provider must be provided.';
    }

    super(saleManager);

    this._paymentProvider = paymentProvider;
    this._appViewManager = AppViewManager.getInstance();
    this._customer = customer;
    this._isDeposit = this._saleManager.deposits.hasDeposits;
    this._amount = amount;

    /**
     * The following unfortunately required due to the nature of how we keep Phone Numbers.
     */
    if (customer instanceof Customer && !customer.isNew()) {
      this._customerContact = this._customer.get('Contact');
      this._customerPhone = this._customer.primaryContactNumber;
    }

    // initialize the controller's managed view
    this.view = new PaymentManualCreditDebitView({
      viewData: {
        currencySymbol: Localize.getCurrencySymbol(),
        saleData: this._saleManager.toJSON(),
        customer: this._customer,
        customerPhone: this._customerPhone,
        gateway: this._paymentProvider.activeGateway,
        amount: this._amount,
        reactAddress: (prefix = '', tabIndex = '') =>
          JSON.stringify({
            rows: [
              {
                label: 'Country',
                name: `${prefix}country`,
                component: 'Country',
                countryCode: get(this._customerContact, 'Addresses.ContactAddress.countryCode', ''),
                countryName: get(this._customerContact, 'Addresses.ContactAddress.country', ''),
                inputProps: {
                  autoComplete: 'nope',
                },
                tabIndex,
              },
              {
                label: 'State',
                name: `${prefix}state`,
                component: 'Region',
                countryCode: get(this._customerContact, 'Addresses.ContactAddress.countryCode', ''),
                regionCode: get(this._customerContact, 'Addresses.ContactAddress.stateCode', ''),
                regionName: get(this._customerContact, 'Addresses.ContactAddress.state', ''),
                inputProps: {
                  autoComplete: 'nope',
                },
                tabIndex,
              },
              {
                label: 'Zip',
                name: `${prefix}zip`,
                value: get(this._customerContact, 'Addresses.ContactAddress.zip', ''),
                placeholder: '',
                tabIndex,
              },
            ],
          }),
      },
    });
  }

  /**
   * Initializes and displays the Credit/Debit Payment view.
   */
  show() {
    super.show();

    // Calling the history change triggers React mount / unmount methods
    window.history.changeState();
  }

  /**
   * Handler for when the "Enter Card Number" button is Pressed.
   * This will take the optional form data and map it properly
   * to what the API expects
   *
   * @param {Array} formData This is the optional form data containing the user info
   */
  onEnterCardNumberClicked(formData) {
    const shippingPrefix = new RegExp('^ship_');
    const manualEntryMapping = this._paymentProvider.manualEntryMapping;

    let manualPaymentParameters = {};
    let billing = {
      Contact: {},
    };
    let shipping = {
      Contact: {},
    };

    this._sale = new Sale();

    manualPaymentParameters.creditCardPresent = true;

    formData.forEach(formEntry => {
      let payloadName = '';
      let contactSection = false;

      manualEntryMapping.forEach(manualEntry => {
        if (
          manualEntry.formName === formEntry.name ||
          manualEntry.formName === formEntry.name.replace(shippingPrefix, '')
        ) {
          payloadName = manualEntry.payloadName;
          contactSection = manualEntry.contactSection;
        }
      });

      if (payloadName && !shippingPrefix.test(formEntry.name)) {
        if (contactSection) {
          billing.Contact[payloadName] = formEntry.value;
        } else {
          billing[payloadName] = formEntry.value;
        }
      } else if (payloadName && shippingPrefix.test(formEntry.name)) {
        if (contactSection) {
          shipping.Contact[payloadName] = formEntry.value;
        } else {
          shipping[payloadName] = formEntry.value;
        }
      }

      if (formEntry.name === CARD_NOT_PRESENT && formEntry.value === ON) {
        manualPaymentParameters.creditCardPresent = false;
      }
    });

    manualPaymentParameters.gateway = this._paymentProvider.activeGateway.get('gatewayID');
    manualPaymentParameters.saleID = this._saleManager.sale.id;
    manualPaymentParameters.balance = this._amount;
    manualPaymentParameters.billing = billing;
    manualPaymentParameters.shipping = shipping;
    manualPaymentParameters.isDeposit = this._isDeposit;

    this._sale.initiateManualEntrySale(manualPaymentParameters).done(response => {
      let url = null;
      let deferred = $.Deferred();

      if (
        this._isDeposit ||
        this._saleManager.hasPendingPayments() ||
        !this._saleManager.deposits.refundTotal.isZero()
      ) {
        // Applies any deposit or pending payments immediately.  Technically, this can unbalance the
        // sale since our deposit amount has been fully reconciled yet but we need it in place
        // so that when the manual credit card payment completes, the sale balances out
        // If the manual credit card payment is cancelled or fails then any payments in the sale
        // will be voided by the server.  Pending payments must be added to the sale immediately
        // as well so that the sale balances once the manual payment returns.
        this._saleManager
          .updateSale({ complete: false })
          .done(() => {
            deferred.resolve();
          })
          .fail(() => {
            deferred.reject();
          });
      } else {
        deferred.resolve();
      }

      deferred.done(() => {
        switch (manualPaymentParameters.gateway) {
          case GatewayConfig.GatewayType.CAYAN:
            url = `${response.paymentURL}?TransportKey=${response.POSTVars.TransportKey}`;
            break;
          case GatewayConfig.GatewayType.ELEMENT_EXPRESS:
            url = response.paymentURL;
            break;
          case GatewayConfig.GatewayType.MERCURY_PAYMENT_SYSTEMS:
            let formHTML =
              `<form action="${response.paymentURL}" method="Post">` +
              `<input type="hidden" name="PaymentID" value="${response.POSTVars.PaymentID}" />` +
              `<input type="hidden" name="ReturnMethod" value="${response.POSTVars.ReturnMethod}" />` +
              '</form>';
            $(formHTML)
              .appendTo('body')
              .trigger('submit');
            break;
          default:
            flashNotifier(
              'warning',
              Localize.translate(
                'There was an issue with your Gateway Settings, please check your configurations and try again'
              ),
              15
            );
            break;
        }

        if (url) {
          Navigation.navigateToUrl(url);
        }
      });
    });
  }
}
